import React, { HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
    variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
    width?: string | number;
    height?: string | number;
    animation?: 'pulse' | 'wave' | 'none';
    count?: number;
}

const Skeleton = ({
    variant = 'text',
    width,
    height,
    animation = 'pulse',
    count = 1,
    className,
    ...props
}: SkeletonProps) => {
    const baseStyles = 'bg-gray-200 dark:bg-gray-700';

    const variantStyles = {
        text: 'rounded h-4',
        circular: 'rounded-full',
        rectangular: 'rounded-none',
        rounded: 'rounded-lg',
    };

    const animationStyles = {
        pulse: 'animate-pulse',
        wave: 'animate-shimmer bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%]',
        none: '',
    };

    const style: React.CSSProperties = {
        width: width ? (typeof width === 'number' ? `${width}px` : width) : undefined,
        height: height ? (typeof height === 'number' ? `${height}px` : height) : undefined,
    };

    const skeletons = Array.from({ length: count }, (_, index) => (
        <div
            key={index}
            className={cn(
                baseStyles,
                variantStyles[variant],
                animationStyles[animation],
                className
            )}
            style={style}
            {...props}
        />
    ));

    return count > 1 ? <div className="space-y-2">{skeletons}</div> : skeletons[0];
};

Skeleton.displayName = 'Skeleton';

// Predefined skeleton patterns for common use cases
export const SkeletonCard = ({ className }: { className?: string }) => {
    return (
        <div className={cn('p-4 space-y-3', className)}>
            <Skeleton variant="rectangular" height={200} />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" />
        </div>
    );
};

export const SkeletonTable = ({ rows = 5, className }: { rows?: number; className?: string }) => {
    return (
        <div className={cn('space-y-3', className)}>
            <Skeleton variant="rectangular" height={40} />
            {Array.from({ length: rows }, (_, i) => (
                <Skeleton key={i} variant="rectangular" height={60} />
            ))}
        </div>
    );
};

export const SkeletonList = ({ items = 5, className }: { items?: number; className?: string }) => {
    return (
        <div className={cn('space-y-4', className)}>
            {Array.from({ length: items }, (_, i) => (
                <div key={i} className="flex items-center gap-3">
                    <Skeleton variant="circular" width={40} height={40} />
                    <div className="flex-1 space-y-2">
                        <Skeleton variant="text" width="70%" />
                        <Skeleton variant="text" width="40%" />
                    </div>
                </div>
            ))}
        </div>
    );
};

export const SkeletonAvatar = ({ size = 40, className }: { size?: number; className?: string }) => {
    return <Skeleton variant="circular" width={size} height={size} className={className} />;
};

export const SkeletonButton = ({ className }: { className?: string }) => {
    return <Skeleton variant="rounded" width={100} height={40} className={className} />;
};

export const SkeletonText = ({ lines = 3, className }: { lines?: number; className?: string }) => {
    return (
        <div className={cn('space-y-2', className)}>
            {Array.from({ length: lines }, (_, i) => (
                <Skeleton
                    key={i}
                    variant="text"
                    width={i === lines - 1 ? '60%' : '100%'}
                />
            ))}
        </div>
    );
};

export default Skeleton;
